"use client";

import { useEffect, useRef, type ReactNode } from "react";

/** 需要滚动入场的区块与卡片（作用于本组件自身子树，非全局 DOM 注入） */
const REVEAL_SELECTORS = [
  "main > section",
  "main > div.section-title",
  ".project-card-grid > article",
  ".home-event-grid > article",
  ".case-home > article",
  ".policy-directory-grid > a",
  ".demand-square-grid > article",
  ".activity-card-grid > article",
  ".opc-grid > article",
  ".service-home > button",
  ".public-opportunity-list > article",
  ".service-catalog > article",
].join(",");

/** 需要跟随指针做 3D 倾斜的元素 */
const TILT_SELECTORS = ".benefit-card, .hero-roles button";

/**
 * 页面动效外壳：滚动入场 + 指针 3D 倾斜 + 顶部导航置顶白底。
 * 取代旧 PublicPage 里对 dangerouslySetInnerHTML 注入 DOM 的 useEffect 增强，
 * 现在作用对象是真实渲染的子树（rootRef），不再查询注入的 HTML。
 */
export default function MotionScope({
  children,
  className,
  route,
}: {
  children: ReactNode;
  className?: string;
  route?: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const cleanups: Array<() => void> = [];
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!reduced && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-in");
              observer.unobserve(entry.target);
            }
          }
        },
        { rootMargin: "0px 0px -8% 0px", threshold: 0 }
      );
      for (const node of root.querySelectorAll<HTMLElement>(REVEAL_SELECTORS)) {
        const index = node.parentElement ? [...node.parentElement.children].indexOf(node) : 0;
        node.dataset.reveal = "";
        node.style.setProperty("--reveal-delay", `${Math.min(index, 5) * 60}ms`);
        observer.observe(node);
      }
      cleanups.push(() => observer.disconnect());
    }

    if (!reduced && window.matchMedia("(hover: hover)").matches) {
      for (const node of root.querySelectorAll<HTMLElement>(TILT_SELECTORS)) {
        const onMove = (event: PointerEvent) => {
          const rect = node.getBoundingClientRect();
          const x = (event.clientX - rect.left) / rect.width - 0.5;
          const y = (event.clientY - rect.top) / rect.height - 0.5;
          node.style.setProperty("--rx", (x * 7).toFixed(2));
          node.style.setProperty("--ry", (y * -7).toFixed(2));
        };
        const onLeave = () => {
          node.style.setProperty("--rx", "0");
          node.style.setProperty("--ry", "0");
        };
        node.addEventListener("pointermove", onMove);
        node.addEventListener("pointerleave", onLeave);
        cleanups.push(() => {
          node.removeEventListener("pointermove", onMove);
          node.removeEventListener("pointerleave", onLeave);
        });
      }
    }

    return () => cleanups.forEach((fn) => fn());
  }, [route]);

  return (
    <div ref={rootRef} className={className} data-route={route}>
      {children}
    </div>
  );
}
